import { cn } from "@repo/ui/lib/utils";
import {
  type ComponentProps,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface ScrollFadeState {
  /** True when the container is scrolled away from the start edge (top for vertical, left for horizontal). */
  canScrollStart: boolean;
  /** True when there is more content past the visible area at the end edge (bottom/right). */
  canScrollEnd: boolean;
}

/**
 * Headless hook that tracks whether a scrollable element has room to scroll
 * in either direction. Use this directly when you need to render fades in a
 * non-standard layout (e.g. a fixed-position container). For most cases prefer
 * the `ScrollFade` component.
 */
export const useScrollFade = (
  ref: RefObject<HTMLElement | null>,
  options: { horizontal?: boolean } = {},
): ScrollFadeState => {
  const { horizontal = false } = options;
  const [state, setState] = useState<ScrollFadeState>({
    canScrollStart: false,
    canScrollEnd: false,
  });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = horizontal ? el.scrollLeft : el.scrollTop;
    const size = horizontal ? el.clientWidth : el.clientHeight;
    const total = horizontal ? el.scrollWidth : el.scrollHeight;
    const canScrollStart = start > 1;
    const canScrollEnd = start + size < total - 1;
    setState((prev) =>
      prev.canScrollStart === canScrollStart &&
      prev.canScrollEnd === canScrollEnd
        ? prev
        : { canScrollStart, canScrollEnd },
    );
  }, [horizontal, ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [measure, ref]);

  return state;
};

export interface ScrollFadeProps extends ComponentProps<"div"> {
  /** Tailwind `from-*` color class matching the surface behind the fade. Default: `from-background`. */
  fromClassName?: string;
  /** Tailwind size class for the fade band (height for vertical, width for horizontal). Default: `h-12` / `w-12`. */
  fadeSizeClassName?: string;
  /** Render children inside a horizontally-scrolling container instead of vertical. Default: false. */
  horizontal?: boolean;
  /** CSS class applied to the outer wrapper (`relative` is always included). */
  wrapperClassName?: string;
}

/**
 * Wraps content in a scrollable container with conditional gradient + blur
 * overlays at the start and end edges. Each overlay fades in only when there
 * is room to scroll in that direction, giving users a visual cue that more
 * content exists beyond the visible area.
 *
 * The wrapper's `className` is forwarded to the inner scroll container so
 * callers can set `max-height`, scrollbar styling, padding, etc.
 */
export const ScrollFade = ({
  children,
  className,
  fromClassName = "from-background",
  fadeSizeClassName,
  horizontal = false,
  wrapperClassName,
  ...props
}: ScrollFadeProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollStart, canScrollEnd } = useScrollFade(scrollRef, {
    horizontal,
  });

  const sizeClass = fadeSizeClassName ?? (horizontal ? "w-12" : "h-12");

  return (
    <div className={cn("relative", wrapperClassName)}>
      <div
        ref={scrollRef}
        className={cn(
          horizontal ? "overflow-x-auto" : "overflow-y-auto",
          className,
        )}
        {...props}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute to-transparent opacity-0 backdrop-blur-[1px] transition-opacity duration-150",
          horizontal
            ? cn(
                "mask-[linear-gradient(to_right,black,transparent)] inset-y-0 left-0 bg-linear-to-r",
                sizeClass,
              )
            : cn(
                "mask-[linear-gradient(to_bottom,black,transparent)] inset-x-0 top-0 bg-linear-to-b",
                sizeClass,
              ),
          fromClassName,
          canScrollStart && "opacity-100",
        )}
      />
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute to-transparent opacity-0 backdrop-blur-[1px] transition-opacity duration-150",
          horizontal
            ? cn(
                "mask-[linear-gradient(to_left,black,transparent)] inset-y-0 right-0 bg-linear-to-l",
                sizeClass,
              )
            : cn(
                "mask-[linear-gradient(to_top,black,transparent)] inset-x-0 bottom-0 bg-linear-to-t",
                sizeClass,
              ),
          fromClassName,
          canScrollEnd && "opacity-100",
        )}
      />
    </div>
  );
};
